<script setup>
/**
 * Admin Departments View
 * Manage Departments and SubDepartments
 */
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ROUTES } from '../../../core/config/constants.js'
import { getFirebaseDb } from '../../../core/firebase/firebase.js'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore'
import LoadingSpinner from '../../components/LoadingSpinner.vue'
import { useFirestoreSubscription } from '@core/composables/useFirestoreSubscription.js'

const { subscribe } = useFirestoreSubscription()

const router = useRouter()

// ============================================
// State
// ============================================
const isLoading = ref(true)
const error = ref(null)
const departments = ref([])
const expandedDepartments = ref(new Set())
const subDepartmentsCache = ref({}) // departmentId -> { loading, error, items }

// Modal state
const showDepartmentModal = ref(false)
const showSubDepartmentModal = ref(false)
const showDeleteModal = ref(false)
const modalMode = ref('add') // 'add' | 'edit'
const editingDepartment = ref(null)
const editingSubDepartment = ref(null)
const deleteTarget = ref(null) // { type: 'department' | 'subdepartment', item, parentId? }

// Form state
const departmentForm = ref({ name: '' })
const subDepartmentForm = ref({ name: '' })
const isSaving = ref(false)
const isDeleting = ref(false)

// Drag and drop state - Departments
const draggedDepartment = ref(null)
const dragOverDepartment = ref(null)
const dropPosition = ref(null) // 'above' | 'below'

// Drag and drop state - SubDepartments
const draggedSubDepartment = ref(null)
const draggedSubParentId = ref(null)
const dragOverSubDepartment = ref(null)
const subDropPosition = ref(null) // 'above' | 'below'

// Touch drag state
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDraggingTouch = ref(false)
const draggedElement = ref(null)
const dragClone = ref(null)
const touchDragType = ref(null) // 'department' | 'subdepartment'

// ============================================
// Computed
// ============================================
const hasDepartments = computed(() => departments.value.length > 0)

// ============================================
// Firestore Subscriptions
// ============================================
function subscribeToDepartments() {
  const db = getFirebaseDb()
  const departmentsRef = collection(db, 'Departments')
  const q = query(departmentsRef, orderBy('order', 'asc'))

  subscribe(
    q,
    (snapshot) => {
      departments.value = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      isLoading.value = false
      error.value = null
    },
    (err) => {
      console.error('[Departments] Subscription error:', err)
      error.value = 'שגיאה בטעינת מחלקות'
      isLoading.value = false
    }
  )
}

async function loadSubDepartments(departmentId) {
  if (subDepartmentsCache.value[departmentId]?.items) {
    return // Already loaded
  }

  subDepartmentsCache.value[departmentId] = { loading: true, error: null, items: [] }

  try {
    const db = getFirebaseDb()
    const subRef = collection(db, 'Departments', departmentId, 'Subs')
    const q = query(subRef, orderBy('order', 'asc'))
    const snapshot = await getDocs(q)

    subDepartmentsCache.value[departmentId] = {
      loading: false,
      error: null,
      items: snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })),
    }
  } catch (err) {
    console.error('[SubDepartments] Load error:', err)
    subDepartmentsCache.value[departmentId] = {
      loading: false,
      error: 'שגיאה בטעינת תת-מחלקות',
      items: [],
    }
  }
}

// ============================================
// Expand/Collapse
// ============================================
function toggleExpand(departmentId) {
  if (expandedDepartments.value.has(departmentId)) {
    expandedDepartments.value.delete(departmentId)
  } else {
    expandedDepartments.value.add(departmentId)
    loadSubDepartments(departmentId)
  }
  expandedDepartments.value = new Set(expandedDepartments.value)
}

function isExpanded(departmentId) {
  return expandedDepartments.value.has(departmentId)
}

function getSubDepartments(departmentId) {
  return subDepartmentsCache.value[departmentId]?.items || []
}

function getSubDepartmentsLoading(departmentId) {
  return subDepartmentsCache.value[departmentId]?.loading || false
}

function getSubDepartmentsCount(departmentId) {
  return subDepartmentsCache.value[departmentId]?.items?.length ?? '...'
}

// ============================================
// Department Modal
// ============================================
function openAddDepartmentModal() {
  modalMode.value = 'add'
  editingDepartment.value = null
  departmentForm.value = { name: '' }
  showDepartmentModal.value = true
}

function openEditDepartmentModal(department) {
  modalMode.value = 'edit'
  editingDepartment.value = department
  departmentForm.value = { name: department.name }
  showDepartmentModal.value = true
}

function closeDepartmentModal() {
  showDepartmentModal.value = false
  editingDepartment.value = null
  departmentForm.value = { name: '' }
}

async function saveDepartment() {
  if (!departmentForm.value.name.trim()) return

  isSaving.value = true

  try {
    const db = getFirebaseDb()

    if (modalMode.value === 'add') {
      // Calculate next order
      const maxOrder = departments.value.reduce((max, d) => Math.max(max, d.order || 0), 0)

      await addDoc(collection(db, 'Departments'), {
        name: departmentForm.value.name.trim(),
        order: maxOrder + 1,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      })
    } else {
      await updateDoc(doc(db, 'Departments', editingDepartment.value.id), {
        name: departmentForm.value.name.trim(),
        updated_at: serverTimestamp(),
      })
    }

    closeDepartmentModal()
  } catch (err) {
    console.error('[Department] Save error:', err)
    alert('שגיאה בשמירת מחלקה: ' + (err.message || 'שגיאה לא ידועה'))
  } finally {
    isSaving.value = false
  }
}

// ============================================
// SubDepartment Modal
// ============================================
function openAddSubDepartmentModal(department) {
  modalMode.value = 'add'
  editingDepartment.value = department
  editingSubDepartment.value = null
  subDepartmentForm.value = { name: '' }
  showSubDepartmentModal.value = true
}

function openEditSubDepartmentModal(department, subDepartment) {
  modalMode.value = 'edit'
  editingDepartment.value = department
  editingSubDepartment.value = subDepartment
  subDepartmentForm.value = { name: subDepartment.name }
  showSubDepartmentModal.value = true
}

function closeSubDepartmentModal() {
  showSubDepartmentModal.value = false
  editingDepartment.value = null
  editingSubDepartment.value = null
  subDepartmentForm.value = { name: '' }
}

async function saveSubDepartment() {
  if (!subDepartmentForm.value.name.trim()) return

  isSaving.value = true

  try {
    const db = getFirebaseDb()
    const departmentId = editingDepartment.value.id

    if (modalMode.value === 'add') {
      const subs = subDepartmentsCache.value[departmentId]?.items || []
      const maxOrder = subs.reduce((max, s) => Math.max(max, s.order || 0), 0)

      await addDoc(collection(db, 'Departments', departmentId, 'Subs'), {
        name: subDepartmentForm.value.name.trim(),
        order: maxOrder + 1,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      })
    } else {
      await updateDoc(doc(db, 'Departments', departmentId, 'Subs', editingSubDepartment.value.id), {
        name: subDepartmentForm.value.name.trim(),
        updated_at: serverTimestamp(),
      })
    }

    // Reload subdepartments
    delete subDepartmentsCache.value[departmentId]
    await loadSubDepartments(departmentId)

    closeSubDepartmentModal()
  } catch (err) {
    console.error('[SubDepartment] Save error:', err)
    alert('שגיאה בשמירת תת-מחלקה: ' + (err.message || 'שגיאה לא ידועה'))
  } finally {
    isSaving.value = false
  }
}

// ============================================
// Delete Modal
// ============================================
function openDeleteDepartmentModal(department) {
  deleteTarget.value = { type: 'department', item: department }
  showDeleteModal.value = true
}

function openDeleteSubDepartmentModal(department, subDepartment) {
  deleteTarget.value = { type: 'subdepartment', item: subDepartment, parentId: department.id }
  showDeleteModal.value = true
}

function closeDeleteModal() {
  showDeleteModal.value = false
  deleteTarget.value = null
}

async function confirmDelete() {
  if (!deleteTarget.value) return

  isDeleting.value = true

  try {
    const db = getFirebaseDb()

    if (deleteTarget.value.type === 'department') {
      const departmentId = deleteTarget.value.item.id

      // First delete all subdepartments
      const subsRef = collection(db, 'Departments', departmentId, 'Subs')
      const subsSnapshot = await getDocs(subsRef)

      for (const subDoc of subsSnapshot.docs) {
        await deleteDoc(doc(db, 'Departments', departmentId, 'Subs', subDoc.id))
      }

      // Then delete the department
      await deleteDoc(doc(db, 'Departments', departmentId))

      // Clean up cache
      delete subDepartmentsCache.value[departmentId]
      expandedDepartments.value.delete(departmentId)
      expandedDepartments.value = new Set(expandedDepartments.value)
    } else {
      // Delete subdepartment
      const parentId = deleteTarget.value.parentId
      await deleteDoc(doc(db, 'Departments', parentId, 'Subs', deleteTarget.value.item.id))

      // Reload subdepartments
      delete subDepartmentsCache.value[parentId]
      await loadSubDepartments(parentId)
    }

    closeDeleteModal()
  } catch (err) {
    console.error('[Delete] Error:', err)
    alert('שגיאה במחיקה: ' + (err.message || 'שגיאה לא ידועה'))
  } finally {
    isDeleting.value = false
  }
}

// ============================================
// Drag and Drop
// ============================================
function onDragStart(event, department) {
  draggedDepartment.value = department
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', department.id)
  // Add slight delay for the dragging class to apply
  requestAnimationFrame(() => {
    event.target.classList.add('dragging')
  })
}

function onDragEnd(event) {
  event.target.classList.remove('dragging')
  draggedDepartment.value = null
  dragOverDepartment.value = null
  dropPosition.value = null
}

function onDragOver(event, department) {
  event.preventDefault()
  if (!draggedDepartment.value || draggedDepartment.value.id === department.id) {
    return
  }

  event.dataTransfer.dropEffect = 'move'
  dragOverDepartment.value = department

  // Determine drop position based on mouse position
  const rect = event.currentTarget.getBoundingClientRect()
  const midY = rect.top + rect.height / 2
  dropPosition.value = event.clientY < midY ? 'above' : 'below'
}

function onDragLeave(event) {
  // Only reset if leaving the row entirely
  if (!event.currentTarget.contains(event.relatedTarget)) {
    dragOverDepartment.value = null
    dropPosition.value = null
  }
}

async function onDrop(event, targetDepartment) {
  event.preventDefault()

  if (!draggedDepartment.value || draggedDepartment.value.id === targetDepartment.id) {
    dragOverDepartment.value = null
    dropPosition.value = null
    return
  }

  const draggedId = draggedDepartment.value.id
  const targetId = targetDepartment.id

  // Calculate new order
  const currentList = [...departments.value]
  const draggedIndex = currentList.findIndex((d) => d.id === draggedId)
  let targetIndex = currentList.findIndex((d) => d.id === targetId)

  // Remove dragged item
  const [draggedItem] = currentList.splice(draggedIndex, 1)

  // Adjust target index if needed (after removal)
  if (draggedIndex < targetIndex) {
    targetIndex--
  }

  // Insert at new position
  const insertIndex = dropPosition.value === 'above' ? targetIndex : targetIndex + 1
  currentList.splice(insertIndex, 0, draggedItem)

  // Update Firestore with new order values
  const db = getFirebaseDb()
  const batch = []

  currentList.forEach((dept, index) => {
    if (dept.order !== index + 1) {
      batch.push(
        updateDoc(doc(db, 'Departments', dept.id), {
          order: index + 1,
        })
      )
    }
  })

  try {
    await Promise.all(batch)
  } catch (err) {
    console.error('[Reorder] Error:', err)
  }

  // Reset drag state
  draggedDepartment.value = null
  dragOverDepartment.value = null
  dropPosition.value = null
}

function getDragClass(department) {
  if (!dragOverDepartment.value || dragOverDepartment.value.id !== department.id) {
    return ''
  }
  return dropPosition.value === 'above' ? 'drop-above' : 'drop-below'
}

// ============================================
// SubDepartment Drag and Drop (Desktop)
// ============================================
function onSubDragStart(event, parentId, subDepartment) {
  draggedSubDepartment.value = subDepartment
  draggedSubParentId.value = parentId
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', subDepartment.id)
  requestAnimationFrame(() => {
    event.target.classList.add('dragging')
  })
}

function onSubDragEnd(event) {
  event.target.classList.remove('dragging')
  draggedSubDepartment.value = null
  draggedSubParentId.value = null
  dragOverSubDepartment.value = null
  subDropPosition.value = null
}

function onSubDragOver(event, parentId, subDepartment) {
  event.preventDefault()
  // Only allow dropping within same parent
  if (!draggedSubDepartment.value || draggedSubParentId.value !== parentId) {
    return
  }
  if (draggedSubDepartment.value.id === subDepartment.id) {
    return
  }

  event.dataTransfer.dropEffect = 'move'
  dragOverSubDepartment.value = subDepartment

  const rect = event.currentTarget.getBoundingClientRect()
  const midY = rect.top + rect.height / 2
  subDropPosition.value = event.clientY < midY ? 'above' : 'below'
}

function onSubDragLeave(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) {
    dragOverSubDepartment.value = null
    subDropPosition.value = null
  }
}

async function onSubDrop(event, parentId, targetSub) {
  event.preventDefault()

  if (!draggedSubDepartment.value || draggedSubParentId.value !== parentId) {
    dragOverSubDepartment.value = null
    subDropPosition.value = null
    return
  }

  if (draggedSubDepartment.value.id === targetSub.id) {
    dragOverSubDepartment.value = null
    subDropPosition.value = null
    return
  }

  await performSubDrop(parentId, draggedSubDepartment.value, targetSub, subDropPosition.value)

  draggedSubDepartment.value = null
  draggedSubParentId.value = null
  dragOverSubDepartment.value = null
  subDropPosition.value = null
}

function getSubDragClass(subDepartment) {
  if (!dragOverSubDepartment.value || dragOverSubDepartment.value.id !== subDepartment.id) {
    return ''
  }
  return subDropPosition.value === 'above' ? 'drop-above' : 'drop-below'
}

async function performSubDrop(parentId, dragged, target, position) {
  if (dragged.id === target.id) return

  const currentList = [...(subDepartmentsCache.value[parentId]?.items || [])]
  const draggedIndex = currentList.findIndex((s) => s.id === dragged.id)
  let targetIndex = currentList.findIndex((s) => s.id === target.id)

  const [draggedItem] = currentList.splice(draggedIndex, 1)

  if (draggedIndex < targetIndex) {
    targetIndex--
  }

  const insertIndex = position === 'above' ? targetIndex : targetIndex + 1
  currentList.splice(insertIndex, 0, draggedItem)

  // Update Firestore
  const db = getFirebaseDb()
  const batch = []

  currentList.forEach((sub, index) => {
    if (sub.order !== index + 1) {
      batch.push(
        updateDoc(doc(db, 'Departments', parentId, 'Subs', sub.id), {
          order: index + 1,
        })
      )
    }
  })

  try {
    await Promise.all(batch)
    // Update local cache
    subDepartmentsCache.value[parentId].items = currentList.map((s, i) => ({
      ...s,
      order: i + 1,
    }))
  } catch (err) {
    console.error('[SubReorder] Error:', err)
  }
}

// ============================================
// Touch Drag and Drop (Mobile) - Departments
// ============================================
function onTouchStart(event, department) {
  if (!event.target.closest('.drag-handle')) return

  event.preventDefault()
  const touch = event.touches[0]
  touchStartY.value = touch.clientY
  touchCurrentY.value = touch.clientY
  draggedDepartment.value = department
  isDraggingTouch.value = true
  draggedElement.value = event.currentTarget
  touchDragType.value = 'department'

  createDragClone(event.currentTarget)
  event.currentTarget.classList.add('dragging')
}

function onTouchMove(event) {
  if (!isDraggingTouch.value) return

  event.preventDefault()
  const touch = event.touches[0]
  touchCurrentY.value = touch.clientY

  if (dragClone.value) {
    dragClone.value.style.top = `${touch.clientY - 20}px`
  }

  if (touchDragType.value === 'department') {
    handleDepartmentTouchMove(touch)
  } else if (touchDragType.value === 'subdepartment') {
    handleSubTouchMove(touch)
  }
}

function handleDepartmentTouchMove(touch) {
  const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY)
  const rowBelow = elementBelow?.closest('.department-row')

  if (rowBelow && rowBelow !== draggedElement.value) {
    const rowIndex = Array.from(rowBelow.parentElement.querySelectorAll('.department-row')).indexOf(
      rowBelow
    )
    const targetDept = departments.value[rowIndex]

    if (targetDept && targetDept.id !== draggedDepartment.value.id) {
      dragOverDepartment.value = targetDept
      const rect = rowBelow.getBoundingClientRect()
      dropPosition.value = touch.clientY < rect.top + rect.height / 2 ? 'above' : 'below'
    }
  } else if (!rowBelow) {
    dragOverDepartment.value = null
    dropPosition.value = null
  }
}

async function onTouchEnd(event) {
  if (!isDraggingTouch.value) return

  if (dragClone.value) {
    dragClone.value.remove()
    dragClone.value = null
  }

  if (draggedElement.value) {
    draggedElement.value.classList.remove('dragging')
  }

  if (touchDragType.value === 'department') {
    if (dragOverDepartment.value && draggedDepartment.value) {
      await performDrop(draggedDepartment.value, dragOverDepartment.value, dropPosition.value)
    }
  } else if (touchDragType.value === 'subdepartment') {
    if (dragOverSubDepartment.value && draggedSubDepartment.value) {
      await performSubDrop(
        draggedSubParentId.value,
        draggedSubDepartment.value,
        dragOverSubDepartment.value,
        subDropPosition.value
      )
    }
  }

  resetTouchState()
}

function resetTouchState() {
  isDraggingTouch.value = false
  touchDragType.value = null
  draggedDepartment.value = null
  dragOverDepartment.value = null
  dropPosition.value = null
  draggedSubDepartment.value = null
  draggedSubParentId.value = null
  dragOverSubDepartment.value = null
  subDropPosition.value = null
  draggedElement.value = null
  touchStartY.value = 0
  touchCurrentY.value = 0
}

function createDragClone(element) {
  const rect = element.getBoundingClientRect()
  const clone = element.cloneNode(true)
  clone.classList.add('drag-clone')
  clone.style.cssText = `
    position: fixed;
    top: ${rect.top}px;
    left: ${rect.left}px;
    width: ${rect.width}px;
    z-index: 10000;
    pointer-events: none;
    opacity: 0.9;
    background: var(--bg-primary);
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    transform: scale(1.02);
  `
  document.body.appendChild(clone)
  dragClone.value = clone
}

// ============================================
// Touch Drag and Drop (Mobile) - SubDepartments
// ============================================
function onSubTouchStart(event, parentId, subDepartment) {
  if (!event.target.closest('.sub-drag-handle')) return

  event.preventDefault()
  const touch = event.touches[0]
  touchStartY.value = touch.clientY
  touchCurrentY.value = touch.clientY
  draggedSubDepartment.value = subDepartment
  draggedSubParentId.value = parentId
  isDraggingTouch.value = true
  draggedElement.value = event.currentTarget
  touchDragType.value = 'subdepartment'

  createDragClone(event.currentTarget)
  event.currentTarget.classList.add('dragging')
}

function handleSubTouchMove(touch) {
  const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY)
  const rowBelow = elementBelow?.closest(
    '.subdepartment-row[data-parent-id="' + draggedSubParentId.value + '"]'
  )

  if (rowBelow && rowBelow !== draggedElement.value) {
    const subId = rowBelow.dataset.subId
    const subs = subDepartmentsCache.value[draggedSubParentId.value]?.items || []
    const targetSub = subs.find((s) => s.id === subId)

    if (targetSub && targetSub.id !== draggedSubDepartment.value.id) {
      dragOverSubDepartment.value = targetSub
      const rect = rowBelow.getBoundingClientRect()
      subDropPosition.value = touch.clientY < rect.top + rect.height / 2 ? 'above' : 'below'
    }
  } else if (!rowBelow) {
    dragOverSubDepartment.value = null
    subDropPosition.value = null
  }
}

async function performDrop(dragged, target, position) {
  if (dragged.id === target.id) return

  const currentList = [...departments.value]
  const draggedIndex = currentList.findIndex((d) => d.id === dragged.id)
  let targetIndex = currentList.findIndex((d) => d.id === target.id)

  // Remove dragged item
  const [draggedItem] = currentList.splice(draggedIndex, 1)

  // Adjust target index if needed
  if (draggedIndex < targetIndex) {
    targetIndex--
  }

  // Insert at new position
  const insertIndex = position === 'above' ? targetIndex : targetIndex + 1
  currentList.splice(insertIndex, 0, draggedItem)

  // Update Firestore
  const db = getFirebaseDb()
  const batch = []

  currentList.forEach((dept, index) => {
    if (dept.order !== index + 1) {
      batch.push(
        updateDoc(doc(db, 'Departments', dept.id), {
          order: index + 1,
        })
      )
    }
  })

  try {
    await Promise.all(batch)
  } catch (err) {
    console.error('[Reorder] Error:', err)
  }
}

// ============================================
// Navigate to Sub Items
// ============================================
function navigateToSubItems(department, sub) {
  router.push({
    name: ROUTES.ADMIN_SUB_ITEMS,
    params: { deptId: department.id, subId: sub.id },
  })
}

// ============================================
// Lifecycle
// ============================================
onMounted(() => {
  subscribeToDepartments()
})
</script>

<template>
  <div class="admin-departments">
    <!-- Page Header -->
    <header class="page-header">
      <div class="header-content">
        <div>
          <h1 class="page-title">ניהול מחלקות</h1>
          <p class="page-subtitle">ניהול מחלקות ותת-מחלקות במערכת</p>
        </div>
        <button v-if="!isLoading && !error" class="btn-add" @click="openAddDepartmentModal">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>הוסף מחלקה</span>
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <div class="content-card">
      <!-- Loading State -->
      <div v-if="isLoading" class="state-container">
        <LoadingSpinner size="lg" />
        <p class="state-text">טוען מחלקות...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="state-container state-error">
        <div class="state-icon">⚠️</div>
        <h3>שגיאה</h3>
        <p class="state-text">{{ error }}</p>
        <button class="btn-retry" @click="subscribeToDepartments">נסה שוב</button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!hasDepartments" class="state-container state-empty">
        <div class="state-icon">📁</div>
        <h3>אין מחלקות עדיין</h3>
        <p class="state-text">הוסף מחלקה חדשה כדי להתחיל</p>
        <button class="btn-add-empty" @click="openAddDepartmentModal">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>הוסף מחלקה</span>
        </button>
      </div>

      <!-- Departments Table -->
      <div v-else class="table-container">
        <table class="departments-table">
          <thead>
            <tr>
              <th class="th-drag"></th>
              <th class="th-expand"></th>
              <th class="th-name">שם מחלקה</th>
              <th class="th-order">סדר</th>
              <th class="th-subs">תת-מחלקות</th>
              <th class="th-actions">פעולות</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="department in departments" :key="department.id">
              <!-- Department Row -->
              <tr
                class="department-row"
                :class="[{ expanded: isExpanded(department.id) }, getDragClass(department)]"
                draggable="true"
                @dragstart="onDragStart($event, department)"
                @dragend="onDragEnd"
                @dragover="onDragOver($event, department)"
                @dragleave="onDragLeave"
                @drop="onDrop($event, department)"
                @touchstart="onTouchStart($event, department)"
                @touchmove="onTouchMove"
                @touchend="onTouchEnd"
              >
                <td class="td-drag">
                  <div class="drag-handle">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                      <circle cx="9" cy="6" r="1.5" />
                      <circle cx="15" cy="6" r="1.5" />
                      <circle cx="9" cy="12" r="1.5" />
                      <circle cx="15" cy="12" r="1.5" />
                      <circle cx="9" cy="18" r="1.5" />
                      <circle cx="15" cy="18" r="1.5" />
                    </svg>
                  </div>
                </td>
                <td class="td-expand">
                  <button class="expand-btn" @click.stop="toggleExpand(department.id)">
                    <svg
                      :class="{ rotated: isExpanded(department.id) }"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </td>
                <td class="td-name">{{ department.name }}</td>
                <td class="td-order">{{ department.order }}</td>
                <td class="td-subs">
                  <span class="subs-count">{{ getSubDepartmentsCount(department.id) }}</span>
                </td>
                <td class="td-actions">
                  <div class="actions-group">
                    <button
                      class="action-btn action-add"
                      title="הוסף תת-מחלקה"
                      @click="openAddSubDepartmentModal(department)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    <button
                      class="action-btn action-edit"
                      title="ערוך"
                      @click="openEditDepartmentModal(department)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      class="action-btn action-delete"
                      title="מחק"
                      @click="openDeleteDepartmentModal(department)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path
                          d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>

              <!-- SubDepartments Rows -->
              <template v-if="isExpanded(department.id)">
                <tr
                  v-if="getSubDepartmentsLoading(department.id)"
                  class="subdepartment-row subdepartment-loading"
                >
                  <td colspan="6">
                    <div class="sub-loading">
                      <LoadingSpinner size="sm" />
                      <span>טוען תת-מחלקות...</span>
                    </div>
                  </td>
                </tr>
                <tr
                  v-else-if="getSubDepartments(department.id).length === 0"
                  class="subdepartment-row subdepartment-empty"
                >
                  <td colspan="6">
                    <div class="sub-empty">אין תת-מחלקות</div>
                  </td>
                </tr>
                <tr
                  v-for="sub in getSubDepartments(department.id)"
                  :key="sub.id"
                  class="subdepartment-row"
                  :class="getSubDragClass(sub)"
                  :data-parent-id="department.id"
                  :data-sub-id="sub.id"
                  draggable="true"
                  @dragstart="onSubDragStart($event, department.id, sub)"
                  @dragend="onSubDragEnd"
                  @dragover="onSubDragOver($event, department.id, sub)"
                  @dragleave="onSubDragLeave"
                  @drop="onSubDrop($event, department.id, sub)"
                  @touchstart="onSubTouchStart($event, department.id, sub)"
                  @touchmove="onTouchMove"
                  @touchend="onTouchEnd"
                >
                  <td class="td-drag">
                    <div class="sub-drag-handle">
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                        <circle cx="9" cy="6" r="1.5" />
                        <circle cx="15" cy="6" r="1.5" />
                        <circle cx="9" cy="12" r="1.5" />
                        <circle cx="15" cy="12" r="1.5" />
                        <circle cx="9" cy="18" r="1.5" />
                        <circle cx="15" cy="18" r="1.5" />
                      </svg>
                    </div>
                  </td>
                  <td class="td-expand"></td>
                  <td class="td-name td-name-sub">
                    <span class="sub-indicator">↳</span>
                    {{ sub.name }}
                  </td>
                  <td class="td-order">{{ sub.order }}</td>
                  <td class="td-subs"></td>
                  <td class="td-actions">
                    <div class="actions-group">
                      <button
                        class="action-btn action-items"
                        title="סדר פריטים"
                        @click="navigateToSubItems(department, sub)"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <rect x="3" y="3" width="7" height="7" rx="1" />
                          <rect x="14" y="3" width="7" height="7" rx="1" />
                          <rect x="3" y="14" width="7" height="7" rx="1" />
                          <rect x="14" y="14" width="7" height="7" rx="1" />
                        </svg>
                      </button>
                      <button
                        class="action-btn action-edit"
                        title="ערוך"
                        @click="openEditSubDepartmentModal(department, sub)"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        class="action-btn action-delete"
                        title="מחק"
                        @click="openDeleteSubDepartmentModal(department, sub)"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path
                            d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Department Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showDepartmentModal" class="modal-overlay" @click="closeDepartmentModal">
          <div class="modal" @click.stop>
            <div class="modal-header">
              <h2>{{ modalMode === 'add' ? 'הוספת מחלקה' : 'עריכת מחלקה' }}</h2>
              <button class="modal-close" @click="closeDepartmentModal">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label for="departmentName">שם מחלקה</label>
                <input
                  id="departmentName"
                  v-model="departmentForm.name"
                  type="text"
                  placeholder="הזן שם מחלקה"
                  :disabled="isSaving"
                  @keyup.enter="saveDepartment"
                />
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn-cancel" :disabled="isSaving" @click="closeDepartmentModal">
                ביטול
              </button>
              <button
                class="btn-save"
                :disabled="isSaving || !departmentForm.name.trim()"
                @click="saveDepartment"
              >
                <LoadingSpinner v-if="isSaving" size="sm" />
                <span v-else>{{ modalMode === 'add' ? 'הוסף' : 'שמור' }}</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- SubDepartment Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showSubDepartmentModal" class="modal-overlay" @click="closeSubDepartmentModal">
          <div class="modal" @click.stop>
            <div class="modal-header">
              <h2>{{ modalMode === 'add' ? 'הוספת תת-מחלקה' : 'עריכת תת-מחלקה' }}</h2>
              <button class="modal-close" @click="closeSubDepartmentModal">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-info">
                <span class="form-info-label">מחלקה:</span>
                <span class="form-info-value">{{ editingDepartment?.name }}</span>
              </div>
              <div class="form-group">
                <label for="subDepartmentName">שם תת-מחלקה</label>
                <input
                  id="subDepartmentName"
                  v-model="subDepartmentForm.name"
                  type="text"
                  placeholder="הזן שם תת-מחלקה"
                  :disabled="isSaving"
                  @keyup.enter="saveSubDepartment"
                />
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn-cancel" :disabled="isSaving" @click="closeSubDepartmentModal">
                ביטול
              </button>
              <button
                class="btn-save"
                :disabled="isSaving || !subDepartmentForm.name.trim()"
                @click="saveSubDepartment"
              >
                <LoadingSpinner v-if="isSaving" size="sm" />
                <span v-else>{{ modalMode === 'add' ? 'הוסף' : 'שמור' }}</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
          <div class="modal modal-delete" @click.stop>
            <div class="modal-header modal-header-delete">
              <h2>אישור מחיקה</h2>
              <button class="modal-close" @click="closeDeleteModal">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="delete-warning">
                <div class="delete-icon">⚠️</div>
                <template v-if="deleteTarget?.type === 'department'">
                  <p>
                    האם אתה בטוח שברצונך למחוק את המחלקה
                    <strong>"{{ deleteTarget.item.name }}"</strong>?
                  </p>
                  <p class="delete-cascade-warning">
                    פעולה זו תמחק גם את כל תתי-המחלקות השייכות למחלקה זו!
                  </p>
                </template>
                <template v-else>
                  <p>
                    האם אתה בטוח שברצונך למחוק את תת-המחלקה
                    <strong>"{{ deleteTarget?.item?.name }}"</strong>?
                  </p>
                </template>
                <p class="delete-final">פעולה זו אינה ניתנת לביטול.</p>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn-cancel" :disabled="isDeleting" @click="closeDeleteModal">
                ביטול
              </button>
              <button class="btn-delete" :disabled="isDeleting" @click="confirmDelete">
                <LoadingSpinner v-if="isDeleting" size="sm" />
                <span v-else>מחק</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.admin-departments {
  padding: var(--spacing-md);
  max-width: 1200px;
  margin: 0 auto;
}

/* ============================================
   Page Header - Compact
   ============================================ */
.page-header {
  margin-bottom: var(--spacing-md);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.page-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

.page-subtitle {
  display: none;
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: var(--gradient-primary);
  color: var(--color-white);
  border: none;
  border-radius: var(--border-radius);
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-add:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.btn-add:active {
  transform: translateY(0);
}

.btn-add svg {
  width: 14px;
  height: 14px;
}

/* ============================================
   Content Card
   ============================================ */
.content-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
}

/* ============================================
   State Containers
   ============================================ */
.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  text-align: center;
  gap: var(--spacing-sm);
}

.state-icon {
  font-size: 32px;
}

.state-container h3 {
  margin: 0;
  font-size: var(--font-size-base);
  color: var(--text-primary);
}

.state-text {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
}

.state-error .state-text {
  color: var(--color-error);
}

.btn-retry,
.btn-add-empty {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: var(--gradient-primary);
  color: var(--color-white);
  border: none;
  border-radius: var(--border-radius);
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all 0.15s;
  margin-top: var(--spacing-xs);
}

.btn-retry:hover,
.btn-add-empty:hover {
  transform: translateY(-1px);
}

/* ============================================
   Table - Compact Working Tool
   ============================================ */
.table-container {
  overflow-x: auto;
}

.departments-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.departments-table th,
.departments-table td {
  padding: 6px 10px;
  text-align: right;
  border-bottom: 1px solid var(--border-color);
}

.departments-table th {
  background: var(--bg-secondary);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 8px 10px;
}

.th-drag {
  width: 28px;
  padding: 6px 2px !important;
}

.th-expand {
  width: 32px;
  padding: 6px 4px !important;
}

.th-name {
  min-width: 150px;
}

.th-order {
  width: 50px;
}

.th-subs {
  width: 60px;
}

/* Drag Handle */
.td-drag {
  width: 28px;
  padding: 4px 2px !important;
  text-align: center;
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--text-muted);
  cursor: grab;
  opacity: 0.4;
  transition: all 0.15s;
  touch-action: none;
  -webkit-touch-callout: none;
}

.department-row:hover .drag-handle {
  opacity: 1;
  color: var(--text-secondary);
}

.drag-handle:active {
  cursor: grabbing;
}

.th-actions {
  width: 100px;
}

/* Department Row */
.department-row {
  transition: all 0.15s ease;
  position: relative;
}

.department-row:hover {
  background: var(--bg-secondary);
}

.department-row.expanded {
  background: rgba(99, 102, 241, 0.04);
}

/* Drag and Drop Effects */
.department-row.dragging {
  opacity: 0.5;
  background: var(--bg-tertiary);
}

.department-row.drop-above {
  box-shadow: inset 0 3px 0 0 var(--color-primary);
  background: rgba(99, 102, 241, 0.08);
}

.department-row.drop-below {
  box-shadow: inset 0 -3px 0 0 var(--color-primary);
  background: rgba(99, 102, 241, 0.08);
}

.department-row.drop-above td:first-child::before,
.department-row.drop-below td:first-child::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--color-primary);
  pointer-events: none;
}

.department-row.drop-above td:first-child::before {
  top: -1px;
}

.department-row.drop-below td:first-child::before {
  bottom: -1px;
}

/* Touch Drag Clone */
.drag-clone {
  border-radius: 4px;
  border: 2px solid var(--color-primary);
  transition: transform 0.1s ease;
}

/* Make drag handle more touch-friendly on mobile */
@media (max-width: 768px) {
  .drag-handle {
    width: 28px;
    height: 28px;
    opacity: 0.7;
  }

  .drag-handle svg {
    width: 16px;
    height: 16px;
  }
}

/* Prevent text selection during drag */
.department-row.dragging * {
  user-select: none;
  -webkit-user-select: none;
}

.td-expand {
  text-align: center;
  padding: 4px !important;
}

.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--text-muted);
  transition: all 0.1s;
}

.expand-btn:hover {
  background: var(--bg-tertiary);
  color: var(--color-primary);
}

.expand-btn svg {
  transition: transform 0.15s;
}

.expand-btn svg.rotated {
  transform: rotate(180deg);
}

.td-name {
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.td-order {
  color: var(--text-muted);
  font-size: 12px;
}

.subs-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--bg-tertiary);
  border-radius: 10px;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
}

/* SubDepartment Row */
.subdepartment-row {
  background: var(--bg-secondary);
  transition: all 0.15s ease;
  position: relative;
}

.subdepartment-row:hover {
  background: var(--bg-tertiary);
}

/* SubDepartment Drag Handle */
.sub-drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: var(--text-muted);
  cursor: grab;
  opacity: 0.3;
  transition: all 0.15s;
  touch-action: none;
  -webkit-touch-callout: none;
}

.subdepartment-row:hover .sub-drag-handle {
  opacity: 0.8;
}

.sub-drag-handle:active {
  cursor: grabbing;
}

/* SubDepartment Drag Effects */
.subdepartment-row.dragging {
  opacity: 0.5;
  background: var(--bg-tertiary);
}

.subdepartment-row.drop-above {
  box-shadow: inset 0 3px 0 0 var(--color-success);
  background: rgba(16, 185, 129, 0.08);
}

.subdepartment-row.drop-below {
  box-shadow: inset 0 -3px 0 0 var(--color-success);
  background: rgba(16, 185, 129, 0.08);
}

.subdepartment-row.dragging * {
  user-select: none;
  -webkit-user-select: none;
}

@media (max-width: 768px) {
  .sub-drag-handle {
    width: 24px;
    height: 24px;
    opacity: 0.6;
  }

  .sub-drag-handle svg {
    width: 14px;
    height: 14px;
  }
}

.td-name-sub {
  padding-right: 24px !important;
}

.sub-indicator {
  color: var(--text-muted);
  margin-left: 6px;
  font-size: 12px;
}

.sub-loading,
.sub-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  color: var(--text-muted);
  font-size: 12px;
}

/* Actions - Compact */
.actions-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  color: var(--text-muted);
  transition: all 0.1s;
}

.action-btn svg {
  width: 14px;
  height: 14px;
}

.action-btn:hover {
  border-color: var(--border-color);
}

.action-add:hover {
  background: rgba(16, 185, 129, 0.1);
  border-color: var(--color-success);
  color: var(--color-success);
}

.action-items:hover {
  background: rgba(245, 158, 11, 0.1);
  border-color: #f59e0b;
  color: #f59e0b;
}

.action-edit:hover {
  background: rgba(99, 102, 241, 0.1);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.action-delete:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: var(--color-error);
  color: var(--color-error);
}

/* ============================================
   Modal - Compact
   ============================================ */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: var(--spacing-md);
}

.modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  width: 100%;
  max-width: 380px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.modal-header h2 {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.modal-header-delete {
  background: linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%);
  border-bottom-color: #fecaca;
}

.modal-header-delete h2 {
  color: #dc2626;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--text-muted);
  transition: all 0.1s;
}

.modal-close svg {
  width: 16px;
  height: 16px;
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--color-error);
}

.modal-body {
  padding: 16px;
}

.form-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--bg-secondary);
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 13px;
}

.form-info-label {
  color: var(--text-muted);
}

.form-info-value {
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.form-group input {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
  font-family: var(--font-family);
  transition: all 0.1s;
}

.form-group input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
}

.form-group input:disabled {
  background: var(--bg-secondary);
  cursor: not-allowed;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.btn-cancel {
  padding: 6px 14px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.1s;
}

.btn-cancel:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 14px;
  min-width: 60px;
  background: var(--gradient-primary);
  color: var(--color-white);
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all 0.1s;
}

.btn-save:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 14px;
  min-width: 60px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: var(--color-white);
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all 0.1s;
}

.btn-delete:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Delete Warning */
.delete-warning {
  text-align: center;
}

.delete-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.delete-warning p {
  margin: 0 0 8px 0;
  color: var(--text-primary);
  font-size: 13px;
}

.delete-warning strong {
  color: var(--color-error);
}

.delete-cascade-warning {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 6px 10px;
  color: #dc2626 !important;
  font-weight: var(--font-weight-medium);
  font-size: 12px;
}

.delete-final {
  font-size: 12px;
  color: var(--text-muted) !important;
  margin-top: 10px !important;
}

/* Modal Transitions - Fast */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}

.modal-fade-enter-active .modal,
.modal-fade-leave-active .modal {
  transition: transform 0.15s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal,
.modal-fade-leave-to .modal {
  transform: scale(0.97);
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 768px) {
  .admin-departments {
    padding: var(--spacing-sm);
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-add {
    width: 100%;
    justify-content: center;
    padding: 8px 12px;
  }

  .departments-table th,
  .departments-table td {
    padding: 4px 6px;
  }

  .th-order,
  .td-order,
  .th-subs,
  .td-subs {
    display: none;
  }

  .action-btn {
    width: 24px;
    height: 24px;
  }

  .action-btn svg {
    width: 12px;
    height: 12px;
  }
}
</style>
